import React, { useCallback } from 'react';
import { useRouter } from 'next/router';
import styled from 'styled-components';
import { space } from 'styled-system';
import { themeGet } from '@styled-system/theme-get';
import { useMediaQuery } from '@react-hook/media-query';
import { useSelector } from 'react-redux';
import selectFilters from 'redux-api/reselect/filters';
import AreaCharts from 'components/area-chart-analytic';
import AssetsRevenues from 'components/assets-revenues-analytic';
import BusinessRevenues from 'components/business-revenues-analytic';
import TopLineCharts from 'components/top-line-charts';
import MapChart from 'components/map-chart';
import Filters from 'components/filters';
import Text from 'theme/text';
import TransationViewTablePreview from 'components/transaction-table/preview';
import ListViewTable from 'components/list-view-table';
import translations from 'translations';
import ListChart from 'components/top-line-charts/list-chart';
import { MdHelpOutline } from 'react-icons/md';
import ToolTip from 'theme/tooltip';
import selectProfile from 'redux-api/reselect/profile';

const Wrapper = styled.div`
  /* The two preview tables at the foot of the page are columns of figures that
     a phone cannot lay out: unusable there, and the detail pages they link to
     are one tap away from the menu. */
  & > .only-wide {
    display: none;
  }

  @media (min-width: ${themeGet('breakpoints.0')}) {
    & > .only-wide {
      display: flex;
    }
  }

  ${space}

  display: flex;
  flex-direction: column;

  & > .row {
    padding: ${themeGet('space.l')}px ${themeGet('space.l')}px 0;
  }

  .wrap-topline-map {
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    margin-top: ${themeGet('space.l')}px;

    @media only screen and (max-width: 1780px) {
      flex-direction: column;
    }

    & > div:first-child {
      margin-right: ${themeGet('space.xxl')}px;

      @media screen and (max-width: 1024px) {
        margin-right: 0;
      }
    }

    & > div {
      width: 100%;

      @media only screen and (max-width: 1780px) {
        margin-bottom: ${themeGet('space.l')}px;
      }

      @media only screen and (min-width: 2100px) {
        flex: 1;
      }
    }

    & > .wrap-map {
      display: none;

      @media only screen and (min-width: 2100px) {
        display: block;
        padding-left: 0;
        margin-right: 0;
        flex: 1;
      }
    }
  }

  & > .row > .map {
    display: block;

    @media only screen and (min-width: 2100px) {
      display: none;
    }
  }
`;

const PieChartSection = styled.div`
  display: flex;
  flex-direction: row;

  @media screen and (max-width: 1024px) {
    flex-direction: column;
  }

  & > div:first-child {
    margin-right: ${themeGet('space.xxl')}px;

    @media screen and (max-width: 1024px) {
      margin-right: 0;
    }
  }

  & > .column {
    flex: 1;
    background: linear-gradient(0deg, #e0f1f3 0%, #f5fafb 72.89%);
    border-radius: 12px;
    padding: ${themeGet('space.xl')}px 0 0 0;
  }
`;

const TitleWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: ${themeGet('space.m')}px;
`;

function PanoramicAnalytics() {
  const { push } = useRouter();
  const matches = useMediaQuery('only screen and (min-width: 2100px)');

  const {
    isAdminAnalytics,
  } = useSelector(selectProfile);

  const { languageCode } = useSelector((state) => state.config);
  const {
    compare,
    dataType,
    dateTo,
    dateFrom,
    dateRange,
    filters,
    terms,
    reloadChecked,
  } = useSelector(selectFilters);

  const handleClickTransactionTableBtn = useCallback(() => {
    push('/analytics/transactions');
  }, [push]);

  const handleClickListTableBtn = useCallback(() => {
    push('/analytics/units');
  }, [push]);

  return (
    <Wrapper>
      <Filters />

      <div className="row">
        <AreaCharts
          dateRange={dateRange}
          dataType={dataType}
          dateFrom={dateFrom}
          dateTo={dateTo}
          compare={compare}
          filters={filters}
          terms={terms}
          reloadChecked={reloadChecked}
          isAdmin={isAdminAnalytics}
        />
      </div>

      <div className="row">
        <PieChartSection>
          <div className="column">
            <Text
              fontSize="h2"
              lineHeight="h2"
              fontWeight="bold"
              color="grey_600"
              textAlign="center"
            >
              {dataType === 'usage_data'
                ? translations[languageCode]['consumption by assets']
                : translations[languageCode]['revenue by assets']}
            </Text>
            <AssetsRevenues
              dateRange={dateRange}
              dataType={dataType}
              dateFrom={dateFrom}
              dateTo={dateTo}
              filters={filters}
              terms={terms}
              compare={compare}
              reloadChecked={reloadChecked}
              isAdmin={isAdminAnalytics}
            />
          </div>
          {dataType === 'usage_data' ? (
            <div className="column">
              <Text
                fontSize="h2"
                lineHeight="h2"
                fontWeight="bold"
                color="grey_600"
                textAlign="center"
              >
                {translations[languageCode]['top titles']}
              </Text>

              <ListChart
                color="main_category"
                size="small"
                term="title"
                dateRange={dateRange}
                dataType={dataType}
                dateFrom={dateFrom}
                dateTo={dateTo}
                compare={compare}
                filters={filters}
                terms={terms}
                reloadChecked={reloadChecked}
                isAdmin={isAdminAnalytics}
              />
            </div>
          ) : (
            <div className="column">
              <Text
                fontSize="h2"
                lineHeight="h2"
                fontWeight="bold"
                color="grey_600"
                textAlign="center"
              >
                {translations[languageCode]['revenues by business model']}
              </Text>
              <BusinessRevenues
                dateRange={dateRange}
                dataType={dataType}
                dateFrom={dateFrom}
                dateTo={dateTo}
                filters={filters}
                terms={terms}
                compare={compare}
                reloadChecked={reloadChecked}
                isAdmin={isAdminAnalytics}
              />
            </div>
          )}
        </PieChartSection>
      </div>

      <div className="row">
        <div className="wrap-topline-map">
          <div className="column">
            <Text
              fontSize="h2"
              lineHeight="h2"
              fontWeight="bold"
              color="grey_600"
              textAlign="center"
              mb="m"
            >
              {translations[languageCode]['Top Charts']}
            </Text>
            <TopLineCharts
              isAdmin={isAdminAnalytics}
              dateRange={dateRange}
              dataType={dataType}
              dateFrom={dateFrom}
              dateTo={dateTo}
              filters={filters}
              terms={terms}
              compare={compare}
              reloadChecked={reloadChecked}
              color={dataType === 'usage_data' ? 'main_category' : 'store_name'}
            />
          </div>

          {dataType !== 'usage_data' && (
            <div className="column">
              <TitleWrapper>
                <Text
                  fontSize="h2"
                  lineHeight="h2"
                  fontWeight="bold"
                  color="grey_600"
                  textAlign="center"
                  mr="xs"
                >
                  {translations[languageCode]['Partners Charts']}
                </Text>
                <MdHelpOutline
                  data-tip
                  data-for="partners_data"
                  size="1em"
                  color="grey_400"
                />
              </TitleWrapper>
              <ToolTip
                forData="partners_data"
                text={translations[languageCode]['tooltip.partners_charts']}
                arrowColor="#098EA0"
                backgroundColor="#098EA0"
                place="right"
                effect="solid"
                html
              />
              <TopLineCharts
                dateRange={dateRange}
                dataType={dataType}
                dateFrom={dateFrom}
                dateTo={dateTo}
                filters={filters}
                terms={terms}
                compare={compare}
                reloadChecked={reloadChecked}
                color="store_name"
                topList="partners"
                isAdmin={isAdminAnalytics}
              />
            </div>
          )}

          {matches && dataType === 'usage_data' && (
            <div className="wrap-map" style={{ marginTop: '60px' }}>
              <div>
                <MapChart
                  dateRange={dateRange}
                  term="country.code"
                  dataType={dataType}
                  dateFrom={dateFrom}
                  dateTo={dateTo}
                  filters={filters}
                  terms={terms}
                  reloadChecked={reloadChecked}
                  isAdmin={isAdminAnalytics}
                />
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="row">
        {matches && dataType !== 'usage_data' && (
          <div className="wrap-map" style={{ marginTop: '60px' }}>
            <div>
              <MapChart
                dateRange={dateRange}
                term="country.code"
                dataType={dataType}
                dateFrom={dateFrom}
                dateTo={dateTo}
                filters={filters}
                terms={terms}
                reloadChecked={reloadChecked}
                isAdmin={isAdminAnalytics}
              />
            </div>
          </div>
        )}
      </div>

      <div className="row only-wide">
        <ListViewTable
          dateRange={dateRange}
          dataType={dataType}
          dateFrom={dateFrom}
          dateTo={dateTo}
          filters={filters}
          terms={terms}
          pagination={false}
          buttonLabel={translations[languageCode]['go details']}
          onButtonClick={handleClickListTableBtn}
          compare={compare}
          reloadChecked={reloadChecked}
          size={5}
          isAdmin={isAdminAnalytics}
        />
      </div>

      <div className="row only-wide">
        <TransationViewTablePreview
          dateRange={dateRange}
          dataType={dataType}
          dateFrom={dateFrom}
          reloadChecked={reloadChecked}
          dateTo={dateTo}
          filters={filters}
          terms={terms}
          pagination={false}
          buttonLabel={translations[languageCode]['go details']}
          onButtonClick={handleClickTransactionTableBtn}
          compare={compare}
          size={5}
          isAdmin={isAdminAnalytics}
        />
      </div>

      <div className="row">
        {!matches && (
          <div className="map">
            <MapChart
              dateRange={dateRange}
              term="country.code"
              dataType={dataType}
              dateFrom={dateFrom}
              dateTo={dateTo}
              filters={filters}
              terms={terms}
              reloadChecked={reloadChecked}
              isAdmin={isAdminAnalytics}
            />
          </div>
        )}
      </div>
    </Wrapper>
  );
}

export default PanoramicAnalytics;
